'use client';

import classNames from 'classnames';
import React, { useState, useEffect, useMemo, useRef } from 'react';

export interface StreamingTextProps {
  text: string;
  speed?: number; // milliseconds per token
  className?: string;
  onComplete?: () => void;
  startDelay?: number; // delay before starting animation
}

// Tokenize HTML into complete tags and text chunks
const tokenizeHtml = (html: string): string[] => {
  const tokens: string[] = [];
  let i = 0;

  while (i < html.length) {
    if (html[i] === '<') {
      // Find the end of the tag
      const tagEnd = html.indexOf('>', i);
      if (tagEnd !== -1) {
        tokens.push(html.slice(i, tagEnd + 1));
        i = tagEnd + 1;
      } else {
        tokens.push(html[i]);
        i++;
      }
    } else {
      // Collect text content word by word
      let textEnd = i;
      while (textEnd < html.length && html[textEnd] !== '<') {
        textEnd++;
      }
      const textContent = html.slice(i, textEnd);
      // Split by words for smoother streaming
      const words = textContent.split(/(\s+)/);
      tokens.push(...words.filter(w => w.length > 0));
      i = textEnd;
    }
  }

  return tokens;
};

export const StreamingText: React.FC<StreamingTextProps> = ({
  text,
  speed = 30,
  className = '',
  onComplete,
  startDelay = 0,
}) => {
  const [currentTokenIndex, setCurrentTokenIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const onCompleteRef = useRef(onComplete);

  // Keep onComplete ref updated without triggering effect
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const tokens = useMemo(() => tokenizeHtml(text || ''), [text]);

  useEffect(() => {
    if (!text) {
      setCurrentTokenIndex(0);
      setIsComplete(false);
      return;
    }

    // Reset animation
    setCurrentTokenIndex(0);
    setIsComplete(false);

    const currentTokens = tokenizeHtml(text);
    const tokenCount = currentTokens.length;
    let interval: NodeJS.Timeout | null = null;

    const startTimer = setTimeout(() => {
      interval = setInterval(() => {
        setCurrentTokenIndex((prevIndex) => {
          const nextIndex = prevIndex + 1;

          if (nextIndex >= tokenCount) {
            if (interval) clearInterval(interval);
            setIsComplete(true);
            onCompleteRef.current?.();
            return tokenCount;
          }

          return nextIndex;
        });
      }, speed);
    }, startDelay);

    return () => {
      clearTimeout(startTimer);
      if (interval) clearInterval(interval);
    };
  }, [text, speed, startDelay]);

  const displayedText = useMemo(() => {
    return tokens.slice(0, currentTokenIndex).join('');
  }, [tokens, currentTokenIndex]);

  return (
    <span className={classNames(className, 'streaming-text')}>
      <span dangerouslySetInnerHTML={{ __html: displayedText }} />
      {!isComplete && (
        <span className="animate-pulse text-primary">|</span>
      )}
    </span>
  );
};
